import React, { useEffect, useState, useRef } from 'react'
import { CSSTransition } from 'react-transition-group'
import { useLocation, useNavigate } from 'react-router-dom'
import {
    Col,
    Container,
    Row,
    TopBar,
    Alert,
    Button,
    Modal,
    EmptyList
} from 'components'
import { useTranslation } from 'react-i18next'
import { useBindDispatch, useAppSelector } from 'hooks'
import {
    constants,
    sendMessageToWorkerWithResponse,
    permissionFlags,
    getConnectUser,
    getStorage,
    openClientFromSearch,
    resolveEnrollmentPath,
    formatPhone
} from 'helpers'
import { findPermission } from 'helpers/findPermission'
import { AddFarmerIcon } from 'assets'
import { FarmerIdModal } from 'pages/farmerId/FarmerIdModal'
import { ClientType } from 'storeTypes'
import { ClientAvatar } from 'components/card/ClientAvatar'
import { getGenderAgeProfile } from 'pages/village/helpers/villageClientHelpers'

export const ClientSearchResult = () => {
    const { pathname } = useLocation()

    const [animate, setAnimate] = useState(false)
    const [animationList, setAnimationList] = useState(false)
    const nodeRef = useRef(null)
    const { t } = useTranslation()
    const searchResult = getStorage(constants.SEARCH_RESULT)
    const { veVillages = [] } = getConnectUser()
    const navigate = useNavigate()
    const [errorMsg, setErrorMsg] = useState('')
    const [searchResultList, setSearchResultList] = useState<ClientType[]>([])
    const [showFarmerIdModal, setShowFarmerIdModal] = useState(false)
    const [selectedClient, setSelectedClient] = useState<ClientType>()

    const {
        villageSelectionInfoHandler,
        selectedClientHandler,
        getClientOrdersHandler
    } = useBindDispatch()

    const { searchResult: searchedClients } = useAppSelector(
        (state) => state.clientSearch
    )

    const { clientOrders } = useAppSelector((state) => state.clientOrders)

    const userPermissions = getStorage(constants.USER_PERMISSIONS)

    const canUseFarmerId = findPermission(
        userPermissions,
        permissionFlags.canUseFarmerId
    )

    useEffect(() => {
        document.title = t('clientSearchResult')

        localStorage.removeItem(constants.CLIENT_DETAIL_LOCAL_STORE)

        if (!searchResult && searchedClients?.length >= 1) {
            localStorage.setItem(
                constants.SEARCH_RESULT,
                JSON.stringify(searchedClients)
            )
            setSearchResultList(searchedClients)
            setAnimate(true)
            return
        }

        if (!searchedClients && !searchResult) {
            // redirect to client search page
            navigate('/client-search')
        }
        setSearchResultList(searchResult || [])
        setAnimate(true)
        localStorage.removeItem(constants.CLIENT_FOR_ENROLLMENT_STORE)
    }, [searchedClients?.length])

    const searchResultClickHandler = (clientData: ClientType) => {
        openClientFromSearch(clientData, {
            canUseFarmerId,
            pathname,
            navigate,
            getClientOrders: getClientOrdersHandler,
            onShowFarmerIdModal: (client) => {
                setSelectedClient(client)
                setShowFarmerIdModal(true)
            }
        })
    }

    const singleResult = searchResultList?.map((searchResult, idx) => {
        const fullname = searchResult.firstname + ' ' + searchResult.lastname
        return (
            <div
                className={
                    animationList
                        ? `card-search--parent animate-show-${idx} animate-showed-${idx}`
                        : `animate-show-${idx}`
                }
                data-testid="search-result"
                key={searchResult.client_code}
                onClick={() => searchResultClickHandler(searchResult)}
            >
                <ClientAvatar
                    genderIconUrl={getGenderAgeProfile(
                        t,
                        searchResult.sex,
                        searchResult?.age || 0
                    )}
                    fullname={fullname}
                    duplicate={searchResult.duplicate}
                />
                <div>
                    <p className="card-search--full-name">{fullname}</p>
                    <p className="card-search--other-content">
                        ID {searchResult.client_code}
                    </p>
                    <p className="card-search--other-content">
                        {searchResult?.goal_items?.length &&
                        searchResult?.all_enrollment > 0
                            ? searchResult.goal_items[0].product.name
                            : '--'}
                    </p>
                    <p className="card-search--other-content">
                        {searchResult.recent_year_delivered
                            ? `${t('lastYearBook')}
                        ${new Date(
                            searchResult.recent_year_delivered
                        ).getFullYear()}`
                            : `${t('village.notDelivered')}`}
                    </p>
                    <p className="card-search--other-content">
                        {t('personalPhone')}:{' '}
                        {searchResult?.phone
                            ? formatPhone(searchResult.phone)
                            : '--'}
                    </p>
                </div>
            </div>
        )
    })

    const enrollmentClickHandler = async () => {
        try {
            const result = await resolveEnrollmentPath({
                veVillages,
                pathname,
                getBlankIdCount: () =>
                    sendMessageToWorkerWithResponse({
                        'get blank id count': 'client needs a blank id'
                    })
            })

            if (result.kind === 'error') {
                setTimeout(() => {
                    setErrorMsg('')
                }, 5000)
                return setErrorMsg(t(result.messageKey))
            }

            if (result.kind === 'navigate') {
                return navigate(result.path)
            }

            villageSelectionInfoHandler({
                url: result.enrollBaseUrl
            })
            return navigate('/village/select-village')
        } catch (error) {
            setErrorMsg(t(error as string))
        }
    }

    const clientSearchResultPage = (
        <CSSTransition
            unmountOnExit
            timeout={constants.ANIMATION_TIMEOUT}
            in={animate}
            classNames="generic"
            appear
            onEnter={() => setAnimationList(true)}
            nodeRef={nodeRef}
        >
            <div ref={nodeRef}>
                <Container>
                    <Modal
                        showModal={showFarmerIdModal}
                        data-testid="farmer-id-modal"
                        position={'center'}
                        className="farmer-id--modal"
                        closeClickHandler={() => setShowFarmerIdModal(false)}
                        showCloseIconBtn
                    >
                        {selectedClient && (
                            <FarmerIdModal
                                clientData={selectedClient}
                                selectClientHandler={selectedClientHandler}
                                closeModalHandler={() =>
                                    setShowFarmerIdModal(false)
                                }
                                clientOrders={clientOrders || []}
                            />
                        )}
                    </Modal>
                    <Row
                        className="client-search--result-containter"
                        data-testid="search-container"
                    >
                        <Col md={12}>
                            <div className="card-search--page-title">
                                {!canUseFarmerId ? (
                                    <h2>
                                        {t('matchedResult')}{' '}
                                        {searchResult?.length > 0
                                            ? `(${searchResult.length})`
                                            : ''}
                                    </h2>
                                ) : (
                                    <p className="card-search--title">
                                        {t('clientSearchResult')}
                                    </p>
                                )}
                            </div>
                        </Col>
                        <Col md={12}>
                            {singleResult?.length > 0 ? (
                                singleResult
                            ) : (
                                <EmptyList message={t('noResult')} />
                            )}
                            {!canUseFarmerId ? (
                                <div
                                    className="card-search--add-new-client"
                                    data-testid="add-new-client-link"
                                    onClick={() => enrollmentClickHandler()}
                                >
                                    <p className="add-new-client-link">
                                        {t('unavailableClient')}
                                    </p>
                                </div>
                            ) : (
                                <Row>
                                    <Col md={12}>
                                        <Button
                                            style="primary"
                                            size="xl"
                                            icon={AddFarmerIcon}
                                            onClick={enrollmentClickHandler}
                                        >
                                            {t('enrollment.addNew')}
                                        </Button>
                                    </Col>
                                </Row>
                            )}
                        </Col>
                    </Row>
                </Container>
            </div>
        </CSSTransition>
    )
    return (
        <>
            <div className="sticky-header" id="sticky-header">
                <Container>
                    <TopBar back backNavigation={() => navigate('/home')}>
                        {t('result')}
                    </TopBar>
                </Container>
            </div>
            {clientSearchResultPage}
            {errorMsg && (
                <Alert type="floating" status={'error'}>
                    {errorMsg}
                </Alert>
            )}
        </>
    )
}
